export function stringToHexColor(input: string): string {
  return intToRGB(hashCode(input));
}

function intToRGB(i: number): string {
  const c = (i & 0x00FFFFFF)
    .toString(16)
    .toUpperCase();
  return '00000'.substring(0, 6 - c.length) + c;
}

export function hashCode(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  return hash;
}

// https://stackoverflow.com/a/66494926
export function stringToHslColor(input: string): string {
  const stringUniqueHash = [...input].reduce((acc, char) => {
    return char.charCodeAt(0) + ((acc << 5) - acc);
  }, 0);
  return `hsl(${stringUniqueHash % 360}, 90%, 50%)`;
}

export function getRgbComponents(cssColor: string): [number, number, number] {
  if (cssColor.startsWith('rgb')) {
    const [, rgb] = cssColor.match(/\(([^)]+)\)/) ?? [];
    if (!rgb) {
      throw new Error('Cannot parse RGB value');
    }
    return rgb.split(/ *, */).slice(0, 3).map(v => parseInt(v)) as [number, number, number];
  }
  if (cssColor.startsWith('hsl')) {
    return hslToRgb(cssColor);
  }
  if (cssColor.startsWith('#')) {
    return hexToRgb(cssColor);
  }
  throw new Error('Cannot parse CSS color value');
}

export function hexToRgb(hex: string): [number, number, number] {
  const digits = hex.length === 4
    ? [hex[1], hex[1], hex[2], hex[2], hex[3], hex[3]]
    : Array.from(hex).slice(1);
  return [
    parseInt('0x' + digits[0] + digits[1]),
    parseInt('0x' + digits[2] + digits[3]),
    parseInt('0x' + digits[4] + digits[5]),
  ];
}

// https://gist.github.com/mjackson/5311256
export function hslToRgb(h: number | string, s?: number, l?: number): [number, number, number] {
  if (typeof h === 'string') {
    const [, hsl] = h.match(/\(([^)]+)\)/) ?? [];
    if (!hsl) {
      throw new Error('Cannot parse HSL value');
    }
    const [hue, sat, lum] = hsl.split(/ *, */);
    h = parseFloat(hue) / 360;
    s = sat.endsWith('%') ? parseFloat(sat) / 100 : parseFloat(sat);
    l = lum.endsWith('%') ? parseFloat(lum) / 100 : parseFloat(lum);
  }

  let r, g, b;
  if (s === 0) {
    r = g = b = l; // achromatic
  } else {
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hue2rgb(p, q, h + 1/3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1/3);
  }

  return [r, g, b].map(v => Math.round(v * 255)) as [number, number, number];
}

/* eslint-disable curly */
function hue2rgb(p: number, q: number, t: number): number {
  if (t < 0) t += 1;
  if (t > 1) t -= 1;
  if (t < 1/6) return p + (q - p) * 6 * t;
  if (t < 1/2) return q;
  if (t < 2/3) return p + (q - p) * (2/3 - t) * 6;
  return p;
}
/* esline-enable curly */

/**
 * Given component RGB values 0-255, compute the luminance of a color.
 * If luminance is < 0.5, use a light text color for proper contrast; else use a dark text color.
 * https://www.w3.org/TR/WCAG20/#relativeluminancedef
 * https://stackoverflow.com/a/56678483
 */
export function computeLuminance(r: number, g: number, b: number): number {
  const rgb = [r, g, b].map(v => {
    const c = v / 255;
    return c < 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * rgb[0] + 0.7152 * rgb[1] + 0.0722 * rgb[2];
}

/**
 * Given a foreground color rendered at an opacity over a background color, compute the resulting blended color.
 * @param rgb component values of the foreground color
 * @param alpha 0-1 opacity ratio
 * @param background component values of the background color
 * @returns component values of the blended color
 */
export function alphaBlend(rgb: [number, number, number], alpha: number, background = [255, 255, 255]): [number, number, number] {
  return rgb.map((color, i) => Math.round(background[i] * (1 - alpha) + color * alpha)) as [number, number, number];
}
